import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Button, Flex } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import isNil from 'lodash/isNil'
import { BigNumber } from 'ethers'
import { BASE_CHAIN_ID_NUMBER, BNToNumberWithDecimals } from '@mprolab/sdk'
import { useGetMproPrice } from 'hooks/useGetMproPrice'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useConnectPanel } from 'contexts/ConnectPanel'
import { useSetupNetwork } from 'hooks/useSetupNetwork'
import useScreenSize from 'hooks/useScreenSize'
import dayjs from 'dayjs'
import { DURATION_LANGUAGE, displayDuration } from 'utils/displayDuration'
import { getDurationInMilliseconds } from 'views/Rewards/rewards.logic'
import { AggregatedClassicStakeConfig, ClassicStakeConfig, ClassicStakeUserConfig } from '../classicStake.logic'
import ColumnComponent from './ColumnComponent'
import { formatPrice } from '../stake.logic'
import { STAKE_OPERATION_TYPE } from '../ClassicStake'

const BorderContainerContrast = styled(Flex)`
  width: 100%;
  padding: 8px 16px;
  border-radius: 8px;
  min-height: 64px;
  background: ${({ theme }) => theme.colors.gray11};
  justify-content: space-between;
  align-items: center;
  ${({ theme }) => theme.mediaQueries.mobile} {
    flex-direction: column;
    gap: 16px;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 8px 12px;
  }
`

const Box = styled(Flex)`
  width: 100%;
  background: ${({ theme }) => theme.colors.gray10};
  border-radius: 8px;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  gap: 24px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

interface StakeInnerTableProps {
  stakeConfig: AggregatedClassicStakeConfig
  loadingData: boolean
  unstake: (stakeAddress: string, amount: BigNumber) => Promise<void>
  claimReward: (stakeAddress: string) => Promise<void>
  fetchClassicStakeConfig: (userAccount?: string, refetch?: boolean) => Promise<void>
  handleStakeOperation: (type: STAKE_OPERATION_TYPE, stake: ClassicStakeConfig, staker: ClassicStakeUserConfig) => void
}

enum ButtonActionType {
  STAKE,
  UNSTAKE,
  CLAIM,
  CLAIM_COUNTER,
  UNSTAKE_COUNTER
}

const StakeInnerTable: React.FC<StakeInnerTableProps> = ({
  stakeConfig,
  loadingData,
  claimReward,
  unstake,
  fetchClassicStakeConfig,
  handleStakeOperation
}) => {
  const { stake, userStakeData: staker } = stakeConfig

  const { t } = useTranslation()
  const { account, chainId } = useWeb3React()
  const { isTablet, isMobile } = useScreenSize()
  const { setConnectPanelOpen } = useConnectPanel()
  const [stakedAmountUSD, setStakedAmountUSD] = useState<number>()
  const [earnedAmountUSD, setEarnedAmountUSD] = useState<number>()
  const [networkSwitching, setNetworkSwitching] = useState(false)
  const [unstakeStartTimestamp, setUnstakeStartTimestamp] = useState(stake?.unstakeStartTimestamp)
  const [claimRewardStartTimestamp, setClaimRewardStartTimestamp] = useState(stake.claimRewardStartTimestamp)
  const [totalStakedAmountUSD, setTotalStakedAmountUSD] = useState<number>()

  const stakeTokenTicker = stake.stakeToken?.token || `${stake.token0?.token}-${stake.token1?.token}`

  const { getMproPrice, mproPriceLoading } = useGetMproPrice()
  const joinedStake = staker?.staked.gt(BigNumber.from(0))
  const { setupNetwork } = useSetupNetwork()

  const invalidChain = chainId !== stakeConfig?.stake?.chainId
  const currentTime = dayjs().unix()

  const disableStakeButton =
    stakeConfig?.stake?.stakeStartTimestamp.toNumber() >= currentTime ||
    stakeConfig?.stake?.stakeEndTimestamp.toNumber() <= currentTime ||
    (!isNil(stake.updateStakersEndTimestamp) && stake.updateStakersEndTimestamp?.toNumber() < currentTime)

  useEffect(() => {
    let intervalId: NodeJS.Timeout
    const updateTimestamp = async () => {
      intervalId = setInterval(() => {
        setClaimRewardStartTimestamp((prevTime) => {
          const newTime = prevTime.sub(BigNumber.from(1))
          if (newTime.lte(BigNumber.from(0))) {
            clearInterval(intervalId)
            return BigNumber.from(0)
          }
          return newTime
        })
        setUnstakeStartTimestamp((prevTime) => {
          const newTime = prevTime.sub(BigNumber.from(1))
          if (newTime.lte(BigNumber.from(0))) {
            clearInterval(intervalId)
            return BigNumber.from(0)
          }
          return newTime
        })
      }, 60000)
    }

    if (!isNil(stake?.unstakeStartTimestamp)) {
      updateTimestamp()
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [stake?.unstakeStartTimestamp, stake.claimRewardStartTimestamp])

  let intervalId: NodeJS.Timeout

  useEffect(() => {
    const updateReward = async () => {
      intervalId = setInterval(() => {
        fetchClassicStakeConfig(account, true)
      }, 30000)
    }
    if (!networkSwitching) updateReward()

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [account, networkSwitching, fetchClassicStakeConfig])

  const skipMproPriceLoading = stake?.stakeTokenPrice && stake?.rewardTokenPrice

  const isLoading = loadingData || (!skipMproPriceLoading && mproPriceLoading)

  useEffect(() => {
    const fetchPrices = async () => {
      let stakedPrice = 0
      let earnedPrice = 0
      if (!isNil(staker)) {
        stakedPrice = stake.stakeTokenPrice
          ? BNToNumberWithDecimals(staker?.staked) * stake.stakeTokenPrice
          : await getMproPrice(staker?.staked, BASE_CHAIN_ID_NUMBER, true)
        earnedPrice = stake.rewardTokenPrice
          ? BNToNumberWithDecimals(staker?.pendingReward) * stake.rewardTokenPrice
          : await getMproPrice(staker?.pendingReward, BASE_CHAIN_ID_NUMBER, true)
      }

      const totalStakedPrice = stake.stakeTokenPrice
        ? BNToNumberWithDecimals(stake?.totalStakedSupply) * stake.stakeTokenPrice
        : await getMproPrice(stake?.totalStakedSupply, BASE_CHAIN_ID_NUMBER, true)
      setStakedAmountUSD(stakedPrice)
      setEarnedAmountUSD(earnedPrice)
      setTotalStakedAmountUSD(totalStakedPrice)
    }
    if (!isNil(stake) || !isNil(staker)) fetchPrices()
  }, [stake.chainId, JSON.stringify(staker), account])

  const getStakeButtonVariant = useCallback(
    (buttonType: ButtonActionType) => {
      switch (true) {
        case invalidChain:
          return {
            buttonDescription: t('stakingPanel.switchNetwork'),
            handleClick: async () => {
              setNetworkSwitching(true)
              clearInterval(intervalId)
              await setupNetwork({ chainId: stakeConfig.stake.chainId, modal: true, error: false })
              setNetworkSwitching(false)
            }
          }
        case buttonType === ButtonActionType.UNSTAKE:
          return {
            buttonDescription: t('stakingPanel.unstake'),
            handleClick: async () => {
              if (stake.version === 'v1') {
                handleStakeOperation(STAKE_OPERATION_TYPE.UNSTAKE, stake, staker)
              } else {
                handleStakeOperation(STAKE_OPERATION_TYPE.UNSTAKE_V2, stake, staker)
              }
            }
          }
        case buttonType === ButtonActionType.CLAIM:
          return {
            buttonDescription: t('stakingPanel.claimReward'),
            handleClick: async () => {
              handleStakeOperation(STAKE_OPERATION_TYPE.CLAIM, stake, staker)
              await claimReward(stake.contractAddress)
              await fetchClassicStakeConfig(account, true)
            }
          }
        case buttonType === ButtonActionType.STAKE:
          return {
            buttonDescription: t('stakingPanel.stake'),
            handleClick: () => handleStakeOperation(STAKE_OPERATION_TYPE.STAKE, stake, staker)
          }
        case buttonType === ButtonActionType.CLAIM_COUNTER:
          const totalClaimTime = claimRewardStartTimestamp.toNumber() - currentTime
          const formattedClaimTime =
            totalClaimTime > 0
              ? displayDuration({
                  time: getDurationInMilliseconds(totalClaimTime),
                  options: { language: DURATION_LANGUAGE.SHORT_EN_EXTENDED }
                })
              : '--:--:--'

          return {
            buttonDescription: `Enabled in ${formattedClaimTime}`
          }
        case unstakeStartTimestamp && buttonType === ButtonActionType.UNSTAKE_COUNTER:
          const totalUnstakeTime = unstakeStartTimestamp?.toNumber() - currentTime
          const formattedUnstakeTime =
            totalUnstakeTime > 0
              ? displayDuration({
                  time: getDurationInMilliseconds(totalUnstakeTime),
                  options: { language: DURATION_LANGUAGE.SHORT_EN_EXTENDED }
                })
              : '--:--:--'
          return {
            buttonDescription: `Enabled in ${formattedUnstakeTime}`
          }
        default:
          return null
      }
    },
    [stake, staker, claimReward, unstake, handleStakeOperation, account]
  )

  const stakeContent = () => {
    switch (true) {
      case isNil(account):
        return (
          <Box>
            <ColumnComponent
              rightAligned
              subValueAligned
              title={t('stakingPanel.personalStaked')}
              value={t('stakingPanel.connectWallet')}
            />
            <Button size="sm" onClick={() => setConnectPanelOpen(true)}>
              {t('stakingPanel.connectWallet')}
            </Button>
          </Box>
        )
      default:
        const incomingUnstake = !isNil(unstakeStartTimestamp) && unstakeStartTimestamp?.toNumber() >= currentTime

        const incomingClaim = stake.version === 'v1' && claimRewardStartTimestamp.toNumber() >= currentTime
        return (
          <Flex gap="16px" fullWidth flexDirection={isTablet ? 'column' : 'row'}>
            <Box>
              <ColumnComponent
                rightAligned={!isMobile}
                title={t('stakingPanel.personalStaked')}
                /*
                 * The STAKE token, in both the number and the name.
                 *
                 * This decoded with the reward token's decimals and labelled
                 * the result with the reward token's ticker. Two stakes in
                 * this table take UNI-V2 LP tokens and pay MPRO, so the row
                 * told those people they had staked MPRO. Naming the wrong
                 * asset is worse than a wrong number: the number invites a
                 * question, the name answers it wrongly.
                 */
                value={`${formatPrice(BNToNumberWithDecimals(staker?.staked, stake.stakeToken?.decimals, 6))} ${
                  stakeTokenTicker
                }`}
                subValue={isNil(stakedAmountUSD) ? '~ $ 0' : `~ $ ${formatPrice(stakedAmountUSD)}`}
                {...{ isLoading }}
              />
              <Flex gap="8px" fullWidth={isMobile}>
                <Button
                  size="sm"
                  onClick={() => getStakeButtonVariant(ButtonActionType.STAKE).handleClick()}
                  fullWidth={isMobile}
                  disabled={disableStakeButton}
                >
                  {getStakeButtonVariant(ButtonActionType.STAKE).buttonDescription}
                </Button>
                {joinedStake && !invalidChain && (
                  <Button
                    onClick={() => getStakeButtonVariant(ButtonActionType.UNSTAKE).handleClick()}
                    size="sm"
                    fullWidth={isMobile}
                    disabled={incomingUnstake || claimRewardStartTimestamp.toNumber() > currentTime}
                  >
                    {
                      getStakeButtonVariant(
                        incomingUnstake ? ButtonActionType.UNSTAKE_COUNTER : ButtonActionType.UNSTAKE
                      ).buttonDescription
                    }
                  </Button>
                )}
              </Flex>
            </Box>
            <Box>
              {/*
                The vesting schedule, said before anybody clicks.
                These stakes release the principal in tranches, and until now
                the only way to discover that was to attempt a withdrawal and
                have it fail. The two numbers that matter are what this wallet
                can take today and when the next tranche opens — both read
                from the contract, neither derived from a percentage whose
                denominator the contract does not expose.
              */}
              {!isNil(stake.nextReleaseAt) && (
                <ColumnComponent
                  rightAligned={!isMobile}
                  title={t('stakingPanel.availableToWithdraw')}
                  value={`${formatPrice(
                    BNToNumberWithDecimals(stake.releasableNow, stake.stakeToken?.decimals, 6)
                  )} ${stakeTokenTicker}`}
                  subValue={t('stakingPanel.nextUnlock', {
                    date: dayjs.unix(stake.nextReleaseAt.toNumber()).format('D MMM, HH:mm')
                  })}
                  {...{ isLoading }}
                />
              )}
              <ColumnComponent
                rightAligned={!isMobile}
                title={t('common.reward')}
                // The mirror image of the same mistake: a reward decoded with
                // the stake token's decimals.
                value={`${formatPrice(BNToNumberWithDecimals(staker?.pendingReward, stake.rewardToken?.decimals, 6))} ${
                  stake.rewardToken?.token
                }`}
                subValue={isNil(earnedAmountUSD) ? '~ $ 0' : `~ $ ${formatPrice(earnedAmountUSD)}`}
                {...{ isLoading }}
              />
              <Button
                fullWidth={isMobile}
                size="sm"
                disabled={
                  invalidChain ? false : staker?.pendingReward.isZero() || isNil(staker?.pendingReward) || incomingClaim
                }
                onClick={() => getStakeButtonVariant(ButtonActionType.CLAIM).handleClick()}
              >
                {
                  getStakeButtonVariant(incomingClaim ? ButtonActionType.CLAIM_COUNTER : ButtonActionType.CLAIM)
                    .buttonDescription
                }
              </Button>
            </Box>
          </Flex>
        )
    }
  }

  return (
    <>
      <BorderContainerContrast mt="16px">
        <Flex>
          {/* Total staked amount */}
          <ColumnComponent
            title={t('stakingPanel.totalStaked')}
            value={`${formatPrice(
              BNToNumberWithDecimals(stake?.totalStakedSupply, stake.stakeToken?.decimals, 6)
            )} ${stakeTokenTicker}`}
            subValue={isNil(totalStakedAmountUSD) ? '~ $ 0' : `~ $ ${formatPrice(totalStakedAmountUSD)}`}
            subValueAligned
            {...{ isLoading }}
          />
        </Flex>
        <Flex fullWidth={isMobile}>{stakeContent()}</Flex>
      </BorderContainerContrast>
    </>
  )
}

export default StakeInnerTable
